import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, InputGroup, Table, Pagination, Spinner, Alert } from 'react-bootstrap';
import { useAuthContext } from '../../contexts/AuthContext';
import apiClient from '../../services/apiClient';
import dateService from '../../services/dateService';

const PatientReportView = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: '',
    imageType: '',
    diseaseType: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    loadReport();
  }, [user]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/api/PatientReport/${user.id}`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      setError('Failed to load medical report. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterImages = (images) => {
    if (!images) return [];
    
    return images.filter(image => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          image.imageName?.toLowerCase().includes(searchLower) ||
          image.description?.toLowerCase().includes(searchLower) ||
          image.doctorName?.toLowerCase().includes(searchLower) ||
          image.radiologistName?.toLowerCase().includes(searchLower) ||
          image.diagnoses?.some(d => 
            d.comment?.toLowerCase().includes(searchLower) ||
            d.diseaseTypes?.some(disease => disease.toLowerCase().includes(searchLower))
          );
        
        if (!matchesSearch) return false;
      }

      if (filters.startDate || filters.endDate) {
        const imageDate = new Date(image.createdAt);
        
        if (filters.startDate && imageDate < new Date(filters.startDate)) {
          return false;
        }
        
        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (imageDate > endDate) return false;
        }
      }

      if (filters.imageType && image.imageTypeName !== filters.imageType) {
        return false;
      }

      if (filters.diseaseType) {
        const hasDiseaseType = image.diagnoses?.some(d => 
          d.diseaseTypes?.some(disease => 
            disease.toLowerCase() === filters.diseaseType.toLowerCase()
          )
        );
        if (!hasDiseaseType) return false;
      }

      return true;
    });
  };

  const getPaginatedImages = (images) => {
    const filtered = filterImages(images);
    const startIndex = (currentPage - 1) * itemsPerPage;
    return {
      images: filtered.slice(startIndex, startIndex + itemsPerPage),
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container className="mt-4">
        <Alert variant="info">No medical report available.</Alert>
      </Container>
    );
  }

  const { images, totalPages } = getPaginatedImages(report.images);

  const imageTypes = [...new Set(report.images?.map(img => img.imageTypeName))];
  
  const diseaseTypes = [...new Set(
    report.images?.flatMap(img => 
      img.diagnoses?.flatMap(d => d.diseaseTypes || []) || []
    )
  )];

  return (
    <Container fluid className="mt-4">
      <Card>
        <Card.Header>
          <h3>Medical Report</h3>
          <div className="text-muted">Patient: {report.patientName}</div>
          <div className="text-muted">Total Cost: ${report.totalCost?.toFixed(2)}</div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4 g-3">
            <Col md={3}>
              <InputGroup>
                <Form.Control
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    search: e.target.value
                  }))}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  startDate: e.target.value
                }))}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  endDate: e.target.value
                }))}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={filters.imageType}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  imageType: e.target.value
                }))}
              >
                <option value="">All Image Types</option>
                {imageTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.diseaseType}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  diseaseType: e.target.value
                }))}
              >
                <option value="">All Disease Types</option>
                {diseaseTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {images.map(image => (
            <Card key={image.imageId} className="mb-4">
              <Card.Header>
                <Row>
                  <Col md={8}>
                    <h5>{image.imageName}</h5>
                    <div className="text-muted">Type: {image.imageTypeName}</div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div>Cost: ${image.imageCost.toFixed(2)}</div>
                    <div>Total with Diagnoses: ${image.totalCost.toFixed(2)}</div>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <img
                      src={image.imageURL}
                      alt={image.imageName}
                      className="img-fluid rounded"
                      style={{ maxHeight: '200px', objectFit: 'contain' }}
                    />
                  </Col>
                  <Col md={8}>
                    <p>{image.description}</p>
                    <div className="mb-2">
                      <strong>Radiologist:</strong> {image.radiologistName || 'N/A'}<br />
                      <strong>Doctor:</strong> {image.doctorName || 'N/A'}<br />
                      <strong>Date:</strong> {dateService.formatDate(image.createdAt)}
                    </div>

                    <h6>Diagnoses:</h6>
                    <Table responsive striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Disease Types</th>
                          <th>Comment</th>
                          <th>Creator</th>
                          <th>Date</th>
                          <th>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {image.diagnoses?.map(diagnosis => (
                          <tr key={diagnosis.diagnosisId}>
                            <td>{diagnosis.diseaseTypes.join(', ')}</td>
                            <td>{diagnosis.comment}</td>
                            <td>
                              {diagnosis.creatorName}<br />
                              <small className="text-muted">{diagnosis.creatorRole}</small>
                            </td>
                            <td>{dateService.formatDate(diagnosis.createdAt)}</td>
                            <td>${diagnosis.cost.toFixed(2)}</td>
                          </tr>
                        ))}
                        {(!image.diagnoses || image.diagnoses.length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center">No diagnoses available</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}

          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PatientReportView;